import { useEffect, useState } from 'react'
import { errorMessage, fetchDaily, fetchEvents, fetchFilters, fetchHourly } from './api'
import { useT } from './i18n'
import {
  DailyResponse,
  DashboardParams,
  EventsResponse,
  FiltersResponse,
  HourlyResponse,
  Resource,
} from './types'

/**
 * Loads one resource whenever `key` changes. A request that is superseded (filters changed) or
 * outlived by the component is aborted, so a slow old response can never overwrite a newer one.
 * `load` is only called for an enabled resource, i.e. hidden sections cost nothing.
 */
function useResource<T>(
  enabled: boolean,
  key: string,
  load: (signal: AbortSignal) => Promise<T>
): Resource<T> {
  const [state, setState] = useState<Resource<T>>({ status: 'idle' })
  const t = useT()

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' })
      return
    }
    const controller = new AbortController()
    setState({ status: 'loading' })
    load(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', data })
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setState({ status: 'error', message: errorMessage(error, t) })
      })
    return () => controller.abort()
    // `load` is recreated every render; `key` identifies the request it makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key])

  return state
}

export function useFilters(): Resource<FiltersResponse> {
  return useResource(true, 'filters', fetchFilters)
}

export type Wanted = { daily: boolean; events: boolean; hourly: boolean }

export function useDashboardData(params: DashboardParams | null, wanted: Wanted) {
  const key = params ? `${params.site}|${params.meterType}|${params.start}|${params.end}` : ''
  const ready = params !== null
  const daily: Resource<DailyResponse> = useResource(
    ready && wanted.daily,
    'daily|' + key,
    (signal) => fetchDaily(params as DashboardParams, signal)
  )
  const events: Resource<EventsResponse> = useResource(
    ready && wanted.events,
    'events|' + key,
    (signal) => fetchEvents(params as DashboardParams, signal)
  )
  const hourly: Resource<HourlyResponse> = useResource(
    ready && wanted.hourly,
    'hourly|' + key,
    (signal) => fetchHourly(params as DashboardParams, signal)
  )
  return { daily, events, hourly }
}
